import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmDialogComponent,
  DeleteDialogData
} from '../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { FetchOptionsStateModel } from '../../core/state/fetchOptions.state';
import { catchError, filter, of, switchMap, tap } from 'rxjs';
import { ModuleActions } from './module.actions';
import { ModuleJsonModel } from '../module.json.model';
import { ModuleService } from '../services/module.service';
import { EditModuleComponent } from '../edit-module/edit-module.component';

export interface ModuleStateModel {
  modules: ModuleJsonModel[]
}

@State<ModuleStateModel>({
  name: 'module',
  defaults: {
    modules: []
  }
})
@Injectable()
export class ModuleState {
  private readonly moduleService = inject(ModuleService);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  @Selector()
  static getState(state: ModuleStateModel) {
    return state;
  }

  @Selector()
  static getModules(state: ModuleStateModel) {
    return state.modules;
  }

  @Selector()
  static getAmountOfModules(state: ModuleStateModel) {
    return state.modules?.length ?? 0;
  }

  @Selector()
  static modulesIsEmpty(state: ModuleStateModel) {
    return (state.modules?.length ?? 0) <= 0;
  }

  @Selector()
  static getSortedModules(state: ModuleStateModel): ModuleJsonModel[] {
    return [...state.modules].sort((a, b) => {
      return a.title.localeCompare(b.title, 'de', { sensitivity: 'base', numeric: true });
    });
  }

  @Action(ModuleActions.Fetch)
  onFetchModules(ctx: StateContext<ModuleStateModel>, action: ModuleActions.Fetch) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);

    const { skipStoreDispatch, ...restOverrides } = action.overrideOptions || {};

    const updatedFetchOptions = {
      ...localFetchOptions,
      ...restOverrides,
      sortFieldName: 'title'
    };

    return this.moduleService.getAllWithPagination({
      queryParams: {
        include: action.include
      }
    }, {
      ...updatedFetchOptions,
      skipStoreDispatch
    }).pipe(
      tap(res => {
        ctx.dispatch(new ModuleActions.FetchSuccess(res[0]));
      })
    );
  }

  @Action(ModuleActions.FetchAll)
  onFetchModulesAll(ctx: StateContext<ModuleStateModel>) {
    return this.moduleService.getMany({}).pipe(
      tap(res => ctx.dispatch(new ModuleActions.FetchSuccess(res.data)))
    );
  }

  @Action(ModuleActions.FetchSuccess)
  onFetchModulesSuccess(ctx: StateContext<ModuleStateModel>, { payload }: ModuleActions.FetchSuccess) {
    ctx.setState({ modules: payload });
  }

  @Action(ModuleActions.Add)
  onAdd(ctx: StateContext<ModuleStateModel>, { payload, filters }: ModuleActions.Add) {
    return this.dialog.open<EditModuleComponent, ModuleJsonModel, ModuleJsonModel>(EditModuleComponent, { data: payload })
      .afterClosed()
      .pipe(
        filter((module): module is ModuleJsonModel => !!module),
        switchMap(module => this.moduleService.createAndSave(module)),
        catchError(() => of()),
        tap(() => {
          if (filters && Object.keys(filters).length > 0) {
            ctx.dispatch(new ModuleActions.FetchWithFilters(filters));
          }
          else {
            ctx.dispatch(new ModuleActions.Fetch());
          }
        })
      );
  }

  @Action(ModuleActions.Edit)
  onEdit(ctx: StateContext<ModuleStateModel>, { payload, filters }: ModuleActions.Edit) {
    return this.dialog.open<EditModuleComponent, ModuleJsonModel, ModuleJsonModel>(EditModuleComponent,
      {
        data: payload,
        width: '80vw',
        maxHeight: '90vh',
        maxWidth: '80vw'
      }
    )
      .afterClosed()
      .pipe(
        filter((module): module is ModuleJsonModel => !!module),
        switchMap(module => this.moduleService.update(module)),
        catchError(() => of()),
        tap(() => {
          if (filters && Object.keys(filters).length > 0) {
            ctx.dispatch(new ModuleActions.FetchWithFilters(filters));
          }
          else {
            ctx.dispatch(new ModuleActions.Fetch());
          }
        })
      );
  }

  @Action(ModuleActions.FetchAppend)
  onFetchAppendModules(ctx: StateContext<ModuleStateModel>, action: ModuleActions.FetchAppend) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);

    const { skipStoreDispatch, ...restOverrides } = action.overrideOptions || {};

    const updatedFetchOptions = {
      ...localFetchOptions,
      ...restOverrides,
      sortFieldName: 'title'
    };

    return this.moduleService.getAllWithPagination({
      queryParams: {
        include: action.include
      }
    }, {
      ...updatedFetchOptions,
      skipStoreDispatch
    }).pipe(
      tap(res => {
        ctx.dispatch(new ModuleActions.FetchSuccessAppend(res[0]));
      })
    );
  }

  @Action(ModuleActions.FetchSuccessAppend)
  onFetchModulesSuccessAppend(ctx: StateContext<ModuleStateModel>, { payload }: ModuleActions.FetchSuccess) {
    const state = ctx.getState();
    ctx.setState({
      modules: [...state.modules, ...payload]
    });
  }

  @Action(ModuleActions.FetchWithFilters)
  onFetchWithFiltersModules(ctx: StateContext<ModuleJsonModel>, action: ModuleActions.FetchWithFilters) {
    return this.moduleService.getMany({
      queryParams: {
        filter: action.filters,
        include: 'consumer,subjects,learningUnits,schoolClasses'
      }
    }).pipe(
      tap(res => ctx.dispatch(new ModuleActions.FetchSuccess(res.data)))
    );
  }

  @Action(ModuleActions.Delete)
  onDelete(ctx: StateContext<ModuleJsonModel>, { payload }: ModuleActions.Delete) {
    return this.dialog.open<ConfirmDialogComponent, DeleteDialogData, boolean>(ConfirmDialogComponent, {
      data: {
        id: payload.id,
        name: payload.title,
        itemType: 'Modul',
        deleteFn: () => {
          return payload.destroy();
        }
      }
    })
      .afterClosed()
      .pipe(
        tap(success => {
          if (!success) return;
          ctx.dispatch(new ModuleActions.Fetch());
        })
      );
  }
}
